/* global messg */
import { kea } from 'kea'
import PropTypes from 'prop-types'
import { put } from 'redux-saga/effects'
import NProgress from 'nprogress'
import { isValidPhoneNumber } from 'react-phone-number-input'

import I18n from 'lib/i18n'
import isEmailValid from 'lib/utils/is-email-valid'
import railsContext from 'lib/selectors/rails-context'
import offlineToggleModeLogic from 'lib/components/offline-toggle-mode/logic'

import pressContactlogic from '../logic'

import controller from '../controller.rb'

export default kea({
  initScene: {
    scene: 'pagesContact',

    transform: payload => ({
      users: payload.users,
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      canAttach: payload.canAttach,
      attachments: payload.attachments
    })
  },

  path: () => ['scenes', 'pagesContact', 'contactForm'],

  connect: {
    props: [
      railsContext,
      ['currentUserId', 'sessionCountry'],
      pressContactlogic,
      ['showLegal'],
      offlineToggleModeLogic,
      ['offlineToggleMode']
    ]
  },

  actions: () => ({
    submit: true,
    messageSent: true,
    sendingError: true,
    setName: name => ({ name }),
    setEmail: email => ({ email }),
    setPhone: phone => ({ phone }),
    setQuestion: question => ({ question }),
    setUser: user => ({ user }),
    setMessage: message => ({ message }),
    updateAttachments: attachments => ({ attachments }),
    removeAttachment: id => ({ id }),
    handleUploadFile: response => ({ response })
  }),

  reducers: ({ actions }) => ({
    users: [[], PropTypes.array, {}],
    name: [
      '',
      PropTypes.string,
      {
        [actions.setName]: (_, { name }) => name
      }
    ],
    email: [
      '',
      PropTypes.string,
      {
        [actions.setEmail]: (_, { email }) => email
      }
    ],
    phone: [
      '',
      PropTypes.string,
      {
        [actions.setPhone]: (_, { phone }) => phone
      }
    ],
    canAttach: [false, PropTypes.bool, {}],
    attachments: [
      [],
      PropTypes.array,
      {
        [actions.updateAttachments]: (_, { attachments }) => attachments,
        [actions.messageSent]: () => [],
        [actions.removeAttachment]: (state, { id }) => {
          return state.map(attachment => (attachment.id === id ? { ...attachment, isRemoving: true } : attachment))
        }
      }
    ],
    question: [
      '',
      PropTypes.string,
      {
        [actions.setQuestion]: (_, { question }) => question,
        [actions.messageSent]: () => ''
      }
    ],
    user: [
      '',
      PropTypes.string,
      {
        [actions.setUser]: (_, { user }) => user,
        [actions.messageSent]: () => ''
      }
    ],
    message: [
      '',
      PropTypes.string,
      {
        [actions.setMessage]: (_, { message }) => message,
        [actions.messageSent]: () => ''
      }
    ],
    isSubmitting: [
      false,
      PropTypes.bool,
      {
        [actions.submit]: () => true,
        [actions.messageSent]: () => false,
        [actions.sendingError]: () => false
      }
    ],
    hasTriedToSubmit: [
      false,
      PropTypes.bool,
      {
        [actions.submit]: () => true,
        [actions.messageSent]: () => false
      }
    ]
  }),

  selectors: ({ selectors }) => ({
    askForName: [
      () => [railsContext, selectors.offlineToggleMode],
      ({ currentUserId }, offlineToggleMode) => !currentUserId || offlineToggleMode,
      PropTypes.bool
    ],
    allErrors: [
      () => [
        selectors.askForName,
        selectors.users,
        selectors.name,
        selectors.email,
        selectors.question,
        selectors.user,
        selectors.message,
        selectors.phone,
        selectors.showLegal
      ],
      (askForName, users, name, email, question, user, message, phone, showLegal) => {
        return {
          name: askForName && !name ? I18n.t('courses.form.error_field_empty') : null,
          email: askForName && (!email || !isEmailValid(email)) ? I18n.t('courses.form.error_field_empty') : null,
          question: !question ? I18n.t('courses.form.error_field_empty') : null,
          user: users && users.length > 0 && user === '' ? I18n.t('courses.form.error_field_empty') : null,
          message: !message ? I18n.t('courses.form.error_field_empty') : null,
          phone:
            !phone && !showLegal
              ? I18n.t('courses.form.error_field_empty')
              : phone && !isValidPhoneNumber(phone)
              ? I18n.t('errors.invalid_phone_number')
              : null
        }
      },
      PropTypes.object
    ],
    hasErrors: [
      () => [selectors.allErrors],
      errors => Object.values(errors).filter(error => error).length > 0,
      PropTypes.bool
    ],
    errors: [
      () => [selectors.allErrors, selectors.hasTriedToSubmit],
      (errors, hasTriedToSubmit) => (hasTriedToSubmit ? errors : {}),
      PropTypes.object
    ]
  }),

  listeners: ({ actions }) => ({
    [actions.handleUploadFile]: async ({ response }) => {
      const { updateAttachments } = actions

      if (response.attachments) {
        await updateAttachments(response.attachments)
      } else {
        messg.error(I18n.t('activerecord.errors.models.user.attributes.avatar_custom.wrong_type'), 4000)
      }
    }
  }),

  takeEvery: ({ actions }) => ({
    [actions.removeAttachment]: function * (action) {
      const { updateAttachments } = this.actions
      const { id } = action.payload

      NProgress.start()
      try {
        const response = yield controller.removeAttachment({ id })

        if (response.sucess) {
          yield put(updateAttachments(response.attachments))
        }
      } catch (error) {
        messg.error(error.message, 4000)
      }

      NProgress.done()
    }
  }),

  takeLatest: ({ actions }) => ({
    [actions.submit]: function * () {
      const { messageSent, sendingError } = this.actions
      const { showLegal } = this.values

      const hasErrors = yield this.get('hasErrors')

      if (hasErrors) {
        yield put(sendingError())
        return
      }

      const { name, email, phone, question, user, message } = yield this.get()

      try {
        const response = yield controller.sendMessage({
          name,
          email,
          phone,
          question,
          message,
          show_legal: showLegal,
          message_thread_user_id: user
        })

        if (response.success) {
          messg.success(response.success, 2500)
          yield put(messageSent())
        } else {
          messg.error(response.error || I18n.t('modals.error_text'), 2500)
          yield put(sendingError())
        }
      } catch (error) {
        messg.error(error.message || I18n.t('modals.error_text'), 2500)
        yield put(sendingError())
      }
    }
  })
})
